/** /tag-categories/:id/:tab? — tag category editor shell. Tabs: General, Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  Typography,
  type ChipOwnProps,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagCategoriesApi from '@/api/tagCategories';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { tagCategoriesHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { categoryCodeActions, categorySchemaActions, categoryUsedByLinks } from './tagLinks';
import { TagCategoryGeneralTab } from './tabs/TagCategoryGeneralTab';
import { TagCategoryVocabularyTab } from './tabs/TagCategoryVocabularyTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'vocabulary', label: 'Vocabulary' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function TagCategoryDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: category,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tag_categories', 'detail', id],
    queryFn: () => tagCategoriesApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => {
    void refetch();
    qc.invalidateQueries({ queryKey: ['tag_categories'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () => tagCategoriesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Category deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_categories'] });
      navigate('/tag-categories');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid category id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !category) return <Alert severity="error">Couldn’t load tag category.</Alert>;

  const hasTags = category.used_by.tags > 0;
  const sourceColor: Record<string, ChipOwnProps['color']> = {
    ref: 'success',
    inline: 'warning',
    none: 'default',
  };
  const schemaChip = (
    <Chip
      size="small"
      variant="outlined"
      color={sourceColor[category.schema_source]}
      label={`schema: ${category.schema_source}`}
    />
  );

  return (
    <Box>
      <PageHeader
        helpStory="tag-categories"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={category.name}
        subtitle={
          <IdentitySubtitle
            tenant={category.tenant}
            tenantFallback="System default"
            grid={
              category.tenant
                ? { href: tagCategoriesHref({ tenant: category.tenant.id }), label: 'Tag categories of this operator' }
                : null
            }
            id={category.id}
            extra={[
              <ValueActions
                key="code"
                actions={categoryCodeActions(category, { tenantId: category.tenant?.id })}
              >
                {category.code}
              </ValueActions>,
            ]}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {category.schema_ref ? (
              <ValueActions chip actions={categorySchemaActions(category.schema_ref)}>
                {schemaChip}
              </ValueActions>
            ) : (
              schemaChip
            )}
            <PermissionGate perm="console_tag_categories_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={hasTags}
                title={hasTags ? 'Has tags — delete/reassign them first.' : undefined}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/tag-categories/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <TagCategoryGeneralTab category={category} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'vocabulary'}>
        <TagCategoryVocabularyTab category={category} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <Stack spacing={1.5} sx={{ maxWidth: 620 }}>
          <Typography variant="body2">
            Live tags in this category: <strong>{category.used_by.tags}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Where those tags are applied. The category matches by its <code>code</code>, so each
            search covers every tag in it — counts per grid are not part of the category record.
          </Typography>
          {categoryUsedByLinks(category, { tenantId: category.tenant?.id }).map((l) => (
            <Box key={l.key}>
              <MuiLink component={RouterLink} to={l.href} underline="hover" sx={{ fontSize: 14 }}>
                {l.label} →
              </MuiLink>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ fontFamily: 'monospace' }}
              >
                {l.hint}
              </Typography>
            </Box>
          ))}
        </Stack>
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.group_tour_tag_category" objectPk={String(category.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={tagCategoriesHref({ tenant: category.tenant?.id })} style={{ color: '#0a1a3f' }}>
          ← Back to tag categories
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this tag category?"
        message={`"${category.name}" will be deleted. This cannot be undone.`}
        confirmLabel="Delete category"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
